import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePageSEO } from '@/hooks/usePageSEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Send, Phone, Mail, Sparkles, X, Search, MessageSquare, FileText, Settings, Users, Zap, Flower2, BookOpen, Video, HelpCircle, Shield } from 'lucide-react';
import EditorialHeader from '@/components/editorial/EditorialHeader';
import EditorialFooter from '@/components/editorial/EditorialFooter';
import EditorialGridLayout from '@/components/editorial/EditorialGridLayout';
import GridLinesWrapper from '@/components/editorial/GridLinesWrapper';
import EditorialSectionDivider from '@/components/editorial/EditorialSectionDivider';
import AnimatedBorderPill from '@/components/editorial/AnimatedBorderPill';
import { useTheme } from '@/contexts/ThemeContext';
import InteractiveParticleOrb from '@/components/animations/InteractiveParticleOrb';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const HelpCenterContent: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  // Theme-aware colors
  const bgColor = isDark ? '#111111' : '#f4f2f1';
  const headingColor = isDark ? '#FFFFFF' : '#111111';
  const textColor = isDark ? '#AAAAAA' : '#555555';
  const cardBgColor = isDark ? '#1A1A1A' : '#FFFFFF';
  const borderColor = isDark ? '#333333' : '#E5E5E5';
  const sectionBg = isDark ? '#111111' : '#f4f2f1';
  const altSectionBg = isDark ? '#0A0A0A' : '#FFFFFF';
  const iconBgColor = isDark ? '#222222' : '#F8F8F8';
  const iconHoverBg = isDark ? '#FFFFFF' : '#111111';
  const inputBg = isDark ? '#1A1A1A' : '#FFFFFF';
  const ctaBtnBg = isDark ? '#FFFFFF' : '#111111';
  const ctaBtnText = isDark ? '#111111' : '#FFFFFF';
  const chatBg = isDark ? '#1A1A1A' : '#FFFFFF';
  const assistantMsgBg = isDark ? '#222222' : '#FAFAFA';
  const assistantMsgBorder = isDark ? '#333333' : '#E5E5E5';
  const userMsgBg = isDark ? '#FFFFFF' : '#111111';
  const userMsgText = isDark ? '#111111' : '#FFFFFF';
  const quickActionBg = isDark ? '#222222' : '#F5F5F5';
  const quickActionBorder = isDark ? '#333333' : '#E5E5E5';
  const typingDotColors = isDark ? ['#666666', '#888888', '#AAAAAA'] : ['#CCCCCC', '#AAAAAA', '#888888'];
  const fadeGradient = isDark 
    ? 'linear-gradient(to top, rgba(26,26,26,1) 0%, rgba(26,26,26,0) 100%)'
    : 'linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)';
  const sendBtnBg = isDark ? '#FFFFFF' : '#111111';
  const sendBtnText = isDark ? '#111111' : '#FFFFFF';
  const sendBtnDisabled = isDark ? '#333333' : '#E5E5E5';
  const sendBtnDisabledText = isDark ? '#666666' : '#888888';
  const gradientText = isDark 
    ? 'bg-gradient-to-r from-[#AAAAAA] via-[#CCCCCC] to-[#FFFFFF]'
    : 'bg-gradient-to-r from-[#555555] via-[#333333] to-[#111111]';
  const searchPlaceholderBg = isDark ? '#222222' : '#F8F8F8';
  const searchBorderDefault = isDark ? '#333333' : '#E5E5E5';
  const shortcutBg = isDark ? '#222222' : '#F8F8F8';
  const shortcutBorder = isDark ? '#333333' : '#E5E5E5';
  const shortcutText = isDark ? '#666666' : '#888888';
  const popularBtnBg = isDark ? '#1A1A1A' : '#FFFFFF';
  const popularBtnBorder = isDark ? '#333333' : '#CCCCCC';
  const numberBg = isDark ? '#222222' : '#F8F8F8';

  const [messages, setMessages] = useState<Message[]>([]);
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [cursorPosition, setCursorPosition] = useState<{ x: number; y: number } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  
  const { toast } = useToast();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  const scrollToInput = () => {
    heroRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  useEffect(() => {
    if (showChat && messages.length > 0) {
      setTimeout(scrollToInput, 100);
    }
  }, [showChat, messages.length]);

  // Track cursor position relative to hero section
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left - rect.width / 2;
        const y = e.clientY - rect.top - rect.height / 2;
        setCursorPosition({ x, y });
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleAISearch = async (queryOverride?: string) => {
    const searchQuery = queryOverride || query;
    
    if (!searchQuery.trim()) {
      toast({
        title: "Please enter a question",
        description: "Type your question in the chat box.",
        variant: "destructive",
      });
      return;
    }

    // Show chat window
    setShowChat(true);

    // Add user message
    const userMessage: Message = { role: 'user', content: searchQuery };
    setMessages(prev => [...prev, userMessage]);
    setQuery('');
    setIsTyping(true);

    try {
      const { data, error } = await supabase.functions.invoke('help-center-ai', {
        body: { query: userMessage.content }
      });

      if (error) {
        throw error;
      }

      // Simulate typing effect
      const response = data.response;
      let currentText = '';
      const words = response.split(' ');
      
      for (let i = 0; i < words.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 50));
        currentText += (i > 0 ? ' ' : '') + words[i];
        setMessages(prev => {
          const newMessages = [...prev];
          if (newMessages[newMessages.length - 1]?.role === 'assistant') {
            newMessages[newMessages.length - 1] = { role: 'assistant', content: currentText };
          } else {
            newMessages.push({ role: 'assistant', content: currentText });
          }
          return newMessages;
        });
      }
    } catch (error) {
      console.error('Error getting AI response:', error);
      toast({
        title: "Something went wrong",
        description: "Please try again or contact our support team.",
        variant: "destructive",
      });
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAISearch();
    }
  };

  const categories = [
    {
      icon: Settings,
      title: "Account settings",
      description: "Manage your profile, security, and preferences"
    },
    {
      icon: Zap,
      title: "Features & Integrations",
      description: "Explore AI tools and third-party connections"
    },
    {
      icon: Users,
      title: "Teams and collaboration",
      description: "Create, manage, and collaborate with your team"
    },
    {
      icon: FileText,
      title: "Content creation",
      description: "AI-powered content tools and best practices"
    },
    {
      icon: Phone,
      title: "Support & troubleshooting",
      description: "Get help with technical issues and bugs"
    },
    {
      icon: Mail,
      title: "Billing & payments",
      description: "Manage plans, invoices, and payment methods"
    }
  ];

  const popularTopics = [
    "AI content creation",
    "lead generation",
    "CRM setup",
    "integrations",
    "pricing plans"
  ];

  return (
    <EditorialGridLayout>
      <div className="min-h-screen overflow-x-hidden" style={{ backgroundColor: bgColor }}>
        <EditorialHeader />
        
        {/* Hero Section */}
        <GridLinesWrapper className={isDark ? 'bg-[#111111]' : 'bg-[#f4f2f1]'}>
          <main className="pt-20">
            <div ref={heroRef} className={`flex flex-col items-center justify-center relative transition-all duration-500 pt-48 ${showChat ? 'min-h-[60vh] pb-8' : 'min-h-screen'}`}>
              {/* Interactive Particle Orb */}
              <InteractiveParticleOrb isAnimating={isTyping} cursorPosition={cursorPosition} />

              <div className={`container mx-auto px-6 text-center max-w-4xl relative z-10 flex flex-col justify-center transition-all duration-500`}>
                {!showChat ? (
                  <>
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 leading-[1.1] tracking-tight mt-20" style={{ color: headingColor }}>
                      How can we help?
                    </h1>
                    
                    <p className="text-sm mb-10 max-w-2xl mx-auto" style={{ color: textColor }}>
                      Get answers to common questions on all things Storily.
                    </p>
                  </>
                ) : null}

                {/* V3 Bento AI Chat Container */}
                {showChat && (
                  <div className="flex-1 max-w-3xl w-full mx-auto mb-6 overflow-hidden animate-fade-in">
                    <div 
                      className="max-h-[520px] flex flex-col overflow-hidden transition-colors duration-300"
                      style={{ 
                        backgroundColor: chatBg,
                        border: `1px solid ${borderColor}`, 
                        borderRadius: '16px',
                        boxShadow: isDark 
                          ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 12px 24px -8px rgba(0, 0, 0, 0.4)'
                          : '0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 12px 24px -8px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      {/* Chat Header */}
                      <div className="px-6 pt-5 pb-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-9 h-9 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: iconBgColor }}
                            >
                              <Sparkles className="h-4 w-4" style={{ color: headingColor }} />
                            </div>
                            <div>
                              <span 
                                className="text-[10px] font-medium px-2 py-0.5 rounded"
                                style={{ backgroundColor: iconBgColor, color: textColor }}
                              >
                                AI Assistant
                              </span>
                              <h3 className="text-xs font-semibold mt-1" style={{ color: headingColor }}>
                                Story Marketing Help
                              </h3>
                            </div>
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => {
                              setShowChat(false);
                              setMessages([]);
                            }}
                            className="h-8 w-8 p-0 rounded-lg"
                            style={{ backgroundColor: 'transparent' }}
                          >
                            <X className="h-4 w-4" style={{ color: textColor }} />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Header Divider */}
                      <div className="h-px w-full" style={{ backgroundColor: borderColor }} />

                      {/* Messages Area with Bottom Fade */}
                      <div className="relative flex-1 overflow-hidden">
                        <div className="p-6 space-y-4 overflow-y-auto h-full max-h-[340px]">
                          {messages.map((message, index) => (
                            <div
                              key={index}
                              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                              {message.role === 'assistant' ? (
                                <div 
                                  className="rounded-xl px-5 py-4 max-w-[85%] animate-fade-in"
                                  style={{ 
                                    backgroundColor: assistantMsgBg,
                                    border: `1px solid ${assistantMsgBorder}`,
                                    color: headingColor
                                  }}
                                >
                                  <p className="text-xs leading-relaxed whitespace-pre-wrap text-left">
                                    {message.content}
                                  </p>
                                </div>
                              ) : (
                                <div 
                                  className="rounded-xl px-5 py-4 max-w-[75%] animate-fade-in"
                                  style={{ 
                                    backgroundColor: userMsgBg,
                                    color: userMsgText
                                  }}
                                >
                                  <p className="text-xs leading-relaxed whitespace-pre-wrap text-right">
                                    {message.content}
                                  </p>
                                </div>
                              )}
                            </div>
                          ))}
                          
                          {/* Suggested Quick Actions after first assistant message */}
                          {messages.length === 1 && messages[0].role === 'assistant' && !isTyping && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {['Pricing', 'Features', 'Getting Started', 'Integrations'].map((topic) => (
                                <button
                                  key={topic}
                                  onClick={() => handleAISearch(topic)}
                                  className="text-[10px] px-2.5 py-1 rounded-lg transition-all"
                                  style={{ 
                                    backgroundColor: quickActionBg, 
                                    color: textColor,
                                    border: `1px solid ${quickActionBorder}`
                                  }}
                                >
                                  {topic}
                                </button>
                              ))}
                            </div>
                          )}
                          
                          {/* Typing Indicator */}
                          {isTyping && (
                            <div className="flex justify-start">
                              <div 
                                className="rounded-xl px-5 py-4 inline-flex items-center gap-1.5"
                                style={{ 
                                  backgroundColor: assistantMsgBg,
                                  border: `1px solid ${assistantMsgBorder}`
                                }}
                              >
                                <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: typingDotColors[0], animationDelay: '0ms' }} />
                                <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: typingDotColors[1], animationDelay: '150ms' }} />
                                <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: typingDotColors[2], animationDelay: '300ms' }} />
                              </div>
                            </div>
                          )}
                          <div ref={messagesEndRef} />
                        </div>
                        
                        {/* Bottom fade overlay */}
                        <div 
                          className="absolute bottom-0 left-0 right-0 h-8 pointer-events-none"
                          style={{ background: fadeGradient }}
                        />
                      </div>

                      {/* Input Divider */}
                      <div className="h-px w-full" style={{ backgroundColor: borderColor }} />

                      {/* Input Area */}
                      <div className="p-4">
                        <div className="relative">
                          <Input
                            placeholder="Ask a question..."
                            className="pr-12 py-3 text-xs rounded-xl transition-all focus:ring-1"
                            style={{ 
                              borderColor: borderColor, 
                              color: headingColor,
                              backgroundColor: inputBg
                            }}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyPress={handleKeyPress}
                          />
                          <Button
                            onClick={() => handleAISearch()}
                            disabled={isLoading || !query.trim()}
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-lg transition-all h-8 w-8 p-0"
                            size="sm"
                            style={{ 
                              backgroundColor: query.trim() ? sendBtnBg : sendBtnDisabled,
                              color: query.trim() ? sendBtnText : sendBtnDisabledText
                            }}
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Search bar - only show when chat is not active */}
                {!showChat && (
                  <div className="w-full max-w-2xl mx-auto mb-8">
                    <div className="relative">
                      <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: textColor }} />
                      <Input
                        placeholder="Search help (e.g. AI content, CRM, pricing)"
                        className="pl-14 pr-16 py-4 text-sm rounded-xl transition-all shadow-sm"
                        style={{ 
                          backgroundColor: inputBg,
                          borderColor: searchBorderDefault,
                          color: headingColor
                        }}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyPress={handleKeyPress}
                      />
                      <div 
                        className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-1 text-[10px] px-2 py-1 rounded"
                        style={{ backgroundColor: shortcutBg, border: `1px solid ${shortcutBorder}`, color: shortcutText }}
                      >
                        <span>⌘</span>
                        <span>K</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Popular searches - only show when chat is not active */}
                {!showChat && (
                  <div className="flex flex-col md:flex-row flex-wrap items-center justify-center gap-2">
                    <span className="text-xs mb-1 md:mb-0" style={{ color: textColor }}>Popular searches:</span>
                    <div className="flex flex-wrap items-center justify-center gap-2">
                    {popularTopics.map((topic) => (
                      <button
                        key={topic}
                        onClick={() => handleAISearch(topic)}
                        className="text-xs px-2.5 py-1 rounded-lg transition-all"
                        style={{ 
                          backgroundColor: popularBtnBg,
                          border: `1px solid ${popularBtnBorder}`,
                          color: textColor
                        }}
                      >
                        {topic}
                      </button>
                    ))}
                    </div>
                  </div>
                )}

                {/* V2 Category Cards - Academy, Agency Owners, Data Security */}
                {!showChat && (
                  <div className="mt-16 max-w-3xl mx-auto">
                    <div className="grid md:grid-cols-3 gap-4">
                      {[
                        { icon: BookOpen, title: "Academy", desc: "Courses to master your marketing", query: "Tell me about the Story Marketing Academy. What courses are available and how can I learn to master marketing?" },
                        { icon: FileText, title: "Agency Owners", desc: "White-Label your own Platform", query: "Tell me about the agency owner white-label platform. How can I use Story Marketing to build my own branded marketing agency?" },
                        { icon: Shield, title: "Data Security", desc: "How do we keep data safe?", query: "Tell me about data security at Story Marketing. How do you store and protect my data? What security measures are in place?" }
                      ].map((item, index) => {
                        const gridLineColor = isDark ? '#444444' : '#d0d0d0';
                        const iconBoxStyle: React.CSSProperties = {
                          backgroundImage: `linear-gradient(to right, ${gridLineColor} 1px, transparent 1px), linear-gradient(to bottom, ${gridLineColor} 1px, transparent 1px)`,
                          backgroundSize: '5px 5px',
                          WebkitMaskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
                          maskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
                        };
                        return (
                          <Card 
                            key={index}
                            onClick={() => handleAISearch(item.query)}
                            className="cursor-pointer group rounded-xl p-5 text-center transition-all hover:shadow-lg"
                            style={{ backgroundColor: cardBgColor, border: `1px solid ${borderColor}` }}
                          >
                            <div 
                              className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 mx-auto transition-transform group-hover:scale-110"
                              style={iconBoxStyle}
                            >
                              <item.icon className="h-5 w-5" style={{ color: isDark ? '#888888' : '#666666' }} strokeWidth={1.5} />
                            </div>
                            <h3 className="font-semibold text-sm mb-1" style={{ color: headingColor }}>
                              {item.title}
                            </h3>
                            <p className="text-xs" style={{ color: textColor }}>
                              {item.desc}
                            </p>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </main>
        </GridLinesWrapper>

        {/* Getting Started Section - Story Marketing 101 */}
        <GridLinesWrapper className={isDark ? 'bg-[#0A0A0A]' : 'bg-[#f4f2f1]'}>
          <section className="py-16 lg:py-20 transition-colors duration-300">
            <div className="max-w-3xl mx-auto px-6 lg:px-8">
              {/* Section Header - Centered */}
              <div className="text-center mb-10">
                <div className="flex justify-center mb-4">
                  <AnimatedBorderPill text="Free Courses" icon={BookOpen} size="sm" />
                </div>
                <h2 className="text-2xl lg:text-3xl font-bold mb-2" style={{ color: headingColor }}>
                  Storily 101.
                </h2>
                <p className="text-sm" style={{ color: textColor }}>
                  Start learning with our most popular courses from the free Academy.
                </p>
              </div>

              {/* Course Cards - Compact V3 */}
              <div className="space-y-2">
                <Link to="/academy" className="block">
                  <Card 
                    className="cursor-pointer group rounded-lg p-4 transition-all hover:shadow-md"
                    style={{ backgroundColor: cardBgColor, border: `1px solid ${borderColor}` }}
                  >
                    <div className="flex items-start gap-3">
                      <div 
                        className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-xs font-semibold transition-all group-hover:scale-105"
                        style={{ backgroundColor: numberBg, color: textColor }}
                      >
                        1
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm mb-0.5" style={{ color: headingColor }}>
                          Content Marketing Mastery
                        </h3>
                        <p className="text-xs" style={{ color: textColor }}>
                          Create compelling content that attracts, engages, and converts your ideal customers.
                        </p>
                      </div>
                    </div>
                  </Card>
                </Link>

                <Link to="/academy" className="block">
                  <Card 
                    className="cursor-pointer group rounded-lg p-4 transition-all hover:shadow-md"
                    style={{ backgroundColor: cardBgColor, border: `1px solid ${borderColor}` }}
                  >
                    <div className="flex items-start gap-3">
                      <div 
                        className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-xs font-semibold transition-all group-hover:scale-105"
                        style={{ backgroundColor: numberBg, color: textColor }}
                      >
                        2
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm mb-0.5" style={{ color: headingColor }}>
                          Email Marketing Fundamentals
                        </h3>
                        <p className="text-xs" style={{ color: textColor }}>
                          Design high-converting email campaigns that nurture leads and drive sales.
                        </p>
                      </div>
                    </div>
                  </Card>
                </Link>

                <Link to="/academy" className="block">
                  <Card 
                    className="cursor-pointer group rounded-lg p-4 transition-all hover:shadow-md"
                    style={{ backgroundColor: cardBgColor, border: `1px solid ${borderColor}` }}
                  >
                    <div className="flex items-start gap-3">
                      <div 
                        className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-xs font-semibold transition-all group-hover:scale-105"
                        style={{ backgroundColor: numberBg, color: textColor }}
                      >
                        3
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm mb-0.5" style={{ color: headingColor }}>
                          AI-Powered Marketing
                        </h3>
                        <p className="text-xs" style={{ color: textColor }}>
                          Leverage AI agents and tools to 10x your marketing productivity and results.
                        </p>
                      </div>
                    </div>
                  </Card>
                </Link>
              </div>

              {/* V3 CTA Button */}
              <div className="mt-6 text-center">
                <Link 
                  to="/academy"
                  className="inline-flex items-center px-4 py-2 text-xs font-semibold rounded-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-sm active:scale-[0.98]"
                  style={{ backgroundColor: ctaBtnBg, color: ctaBtnText }}
                >
                  Join Our Academy →
                </Link>
              </div>
            </div>
          </section>
        </GridLinesWrapper>

        <EditorialSectionDivider />

        {/* Browse by Category / FAQs Section */}
        <GridLinesWrapper className={isDark ? 'bg-[#111111]' : 'bg-[#f4f2f1]'}>
          <section className="py-16 lg:py-20 transition-colors duration-300">
            <div className="max-w-4xl mx-auto px-6 lg:px-8">
              {/* Section Header - Centered */}
              <div className="text-center mb-10">
                <div className="flex justify-center mb-4">
                  <AnimatedBorderPill text="FAQs" icon={HelpCircle} size="sm" />
                </div>
                <h2 className="text-2xl lg:text-3xl font-bold mb-2" style={{ color: headingColor }}>
                  Find answers in our FAQs.
                </h2>
                <p className="text-sm" style={{ color: textColor }}>
                  Browse by category to find what you need.
                </p>
              </div>

              {/* FAQ Category Grid - Compact V3 Cards */}
              <div className="grid md:grid-cols-3 gap-4">
                {categories.map((category, index) => {
                  const gridLineColor = isDark ? '#444444' : '#d0d0d0';
                  const iconBoxStyle: React.CSSProperties = {
                    backgroundImage: `linear-gradient(to right, ${gridLineColor} 1px, transparent 1px), linear-gradient(to bottom, ${gridLineColor} 1px, transparent 1px)`,
                    backgroundSize: '5px 5px',
                    WebkitMaskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
                    maskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
                  };
                  return (
                    <Link
                      key={category.title}
                      to="/faq"
                      className="block"
                    >
                      <Card
                        className="cursor-pointer group rounded-xl p-5 text-center h-full transition-all hover:shadow-lg"
                        style={{ backgroundColor: cardBgColor, border: `1px solid ${borderColor}` }}
                      >
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 mx-auto transition-transform group-hover:scale-110"
                          style={iconBoxStyle}
                        >
                          <category.icon className="h-5 w-5" style={{ color: isDark ? '#888888' : '#666666' }} strokeWidth={1.5} />
                        </div>
                        <h3 className="font-semibold text-sm mb-1" style={{ color: headingColor }}>
                          {category.title}
                        </h3>
                        <p className="text-xs" style={{ color: textColor }}>
                          {category.description}
                        </p>
                      </Card>
                    </Link>
                  );
                })}
              </div>
              
              {/* View All FAQs Button - V3 style */}
              <div className="mt-10 text-center">
                <Link 
                  to="/faq"
                  className="inline-block px-5 py-2.5 rounded-lg text-xs font-semibold transition-all duration-200 hover:scale-[1.02] hover:shadow-md hover:opacity-90 active:scale-[0.98]"
                  style={{ backgroundColor: ctaBtnBg, color: ctaBtnText }}
                >
                  View all FAQs
                </Link>
              </div>
            </div>
          </section>
        </GridLinesWrapper>

        <EditorialFooter />
      </div>
    </EditorialGridLayout>
  );
};

const HelpCenter: React.FC = () => {
  usePageSEO({
    title: 'Help Center & Support | Story Marketing Group',
    description: 'Get help with your marketing platform. AI-powered support, tutorials, FAQs, and live assistance available.',
    canonical: 'https://storymarketing.group/help',
    keywords: 'help center, customer support, platform help, marketing support, AI assistant'
  });

  return <HelpCenterContent />;
};

export default HelpCenter;
